import React, {useState, useRef} from 'react';
import ReactDOM from 'react-dom';
import {autoUpdate, Placement, shift, useFloating} from '@floating-ui/react';

import {useDomEvent} from 'common/hooks/useDomEvent';
import {DisplaySize} from 'common/components/tooltip/types';
import {TooltipReact} from 'common/components/tooltip';

import styles from './new-tooltip.module.styl';

type Props = {
  text: string;
  size: DisplaySize;
  placement: Placement;
  /** Whether to show the tooltip or not. */
  enabled?: boolean;
  /** The element to add a tooltip to. */
  children?: React.ReactElement;
  /** If you want to anchor to something other than the child. NOTE: Prefer to use child though. */
  anchorRef?: HTMLElement;
};

export function NewTooltip(props: Props) {
  const [showTooltip, setShowTooltip] = useState(false);
  const childRef = useRef<HTMLElement | null>(null);

  const element = childRef.current || props.anchorRef;
  const enabled = (props.enabled !== undefined ? props.enabled : true) && props.text;

  const root = document.querySelector(`#tooltips`) || document.body;

  const {refs, floatingStyles} = useFloating({
    placement: props.placement,
    middleware: [shift()],
    whileElementsMounted: autoUpdate,
    elements: {reference: element},
  });

  useDomEvent(element, `mouseenter`, () => setShowTooltip(true));
  useDomEvent(element, `mouseleave`, () => setShowTooltip(false));

  const clonedChild = props.children
    ? React.cloneElement(props.children, {
        ref: (el: any) => {
          childRef.current = el;

          // preserve any ref that might be passed in to the child
          const originalRef = (props.children as any)?.ref;
          if (typeof originalRef === `function`) {
            originalRef(el);
          } else if (originalRef) {
            originalRef.current = el;
          }
        },
      })
    : null;

  return (
    <>
      {clonedChild}
      {showTooltip &&
        enabled &&
        ReactDOM.createPortal(
          <div className={styles.tooltipContainer} ref={refs.setFloating} style={{...floatingStyles}}>
            <TooltipReact forceShow text={props.text} placement={props.placement as any} size={props.size} />
          </div>,
          root,
        )}
    </>
  );
}
